import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface User {
  _id: string;
  email?: string;
  phone?: string;
  provider?: string;
  auth0User?: string;
  superuser?: boolean;
  instagram?: {
    username?: string;
  };
  referralCode?: string;
  phoneDetails?: {
    city?: string;
    state?: string;
    countryCode?: string;
    lat?: number;
    lng?: number;
  };
  ipDetails?: {
    ip?: string;
    country?: string;
    state?: string;
    city?: string;
    postalCode?: string;
    lat?: number;
    lng?: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

interface RelatedDocumentCount {
  name: string;
  count: number;
}

interface RelatedDocuments {
  collections: RelatedDocumentCount[];
  totalCount: number;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function formatDate(value?: string) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function DetailRow({ label, value }: { label: string; value: string }) {
  const isMultiline = value.includes("\n");

  return (
    <div className="grid grid-cols-[160px_1fr] gap-4 py-3 border-b last:border-b-0">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`text-sm break-all ${isMultiline ? "whitespace-pre-wrap font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}

export function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [relatedDocuments, setRelatedDocuments] = useState<RelatedDocuments | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;

    async function fetchUser() {
      try {
        const response = await fetch(`/api/admin/users/${userId}`);
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setRelatedDocuments(data.relatedDocuments ?? null);
        } else if (response.status === 404) {
          setError("User not found");
        } else {
          setError("Failed to fetch user");
        }
      } catch (err) {
        console.error("Failed to fetch user:", err);
        setError("Failed to fetch user");
      } finally {
        setIsLoading(false);
      }
    }

    fetchUser();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    const label = user?.email || user?._id || "User";

    setBreadcrumbs([
      { label: "Admin", path: "/admin" },
      { label: "Users", path: "/admin/users" },
      { label, path: `/admin/users/${userId}` },
    ]);

    return () => {
      clearBreadcrumbs();
    };
  }, [userId, user?.email, user?._id]);

  async function handleDeleteAllData() {
    if (!userId) return;

    const totalCount = relatedDocuments?.totalCount ?? 0;
    const confirmed = window.confirm(
      `Are you sure you want to delete all data related to this user? This will permanently delete ${totalCount} document${totalCount === 1 ? "" : "s"} and cannot be undone.`
    );
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        navigate("/admin/users");
      } else {
        const data = await response.json().catch(() => null);
        alert(data?.error || "Failed to delete user data");
      }
    } catch (err) {
      console.error("Failed to delete user data:", err);
      alert("Failed to delete user data");
    } finally {
      setIsDeleting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground">{error || "User not found"}</p>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-3xl">
      <h1 className="text-2xl font-semibold mb-6">
        Edit User
      </h1>

      <div className="mb-8">
        <h2 className="text-sm font-medium mb-2">Account</h2>
        <div className="border rounded-md px-4">
          <DetailRow label="ID" value={formatValue(user._id)} />
          <DetailRow label="Email" value={formatValue(user.email)} />
          <DetailRow label="Phone" value={formatValue(user.phone)} />
          <DetailRow label="Provider" value={formatValue(user.provider)} />
          <DetailRow label="Auth0 User" value={formatValue(user.auth0User)} />
          <DetailRow label="Superuser" value={formatValue(user.superuser)} />
          <DetailRow
            label="Instagram"
            value={formatValue(user.instagram?.username)}
          />
          <DetailRow
            label="Referral code"
            value={formatValue(user.referralCode)}
          />
          <DetailRow label="Created" value={formatDate(user.createdAt)} />
          <DetailRow label="Updated" value={formatDate(user.updatedAt)} />
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-sm font-medium mb-2">Phone details</h2>
        <div className="border rounded-md px-4">
          <DetailRow label="City" value={formatValue(user.phoneDetails?.city)} />
          <DetailRow label="State" value={formatValue(user.phoneDetails?.state)} />
          <DetailRow
            label="Country code"
            value={formatValue(user.phoneDetails?.countryCode)}
          />
          <DetailRow label="Latitude" value={formatValue(user.phoneDetails?.lat)} />
          <DetailRow label="Longitude" value={formatValue(user.phoneDetails?.lng)} />
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-sm font-medium mb-2">IP details</h2>
        <div className="border rounded-md px-4">
          <DetailRow label="IP" value={formatValue(user.ipDetails?.ip)} />
          <DetailRow label="Country" value={formatValue(user.ipDetails?.country)} />
          <DetailRow label="State" value={formatValue(user.ipDetails?.state)} />
          <DetailRow label="City" value={formatValue(user.ipDetails?.city)} />
          <DetailRow
            label="Postal code"
            value={formatValue(user.ipDetails?.postalCode)}
          />
          <DetailRow label="Latitude" value={formatValue(user.ipDetails?.lat)} />
          <DetailRow label="Longitude" value={formatValue(user.ipDetails?.lng)} />
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-sm font-medium mb-2">Related documents</h2>
        {relatedDocuments && relatedDocuments.collections.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Collection</TableHead>
                <TableHead className="text-right">Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {relatedDocuments.collections.map((collection) => (
                <TableRow key={collection.name}>
                  <TableCell>{collection.name}</TableCell>
                  <TableCell className="text-right">{collection.count}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell className="font-medium">Total</TableCell>
                <TableCell className="text-right font-medium">
                  {relatedDocuments.totalCount}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground">No related documents found.</p>
        )}
      </div>

      <Button
        variant="destructive"
        onClick={handleDeleteAllData}
        disabled={isDeleting}
      >
        {isDeleting && <Loader2 className="animate-spin" />}
        Delete All Data
      </Button>
    </div>
  );
}
